import { Button, Grid, Link, TextField, Theme, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { T, useT } from 'react-polyglot-hooks';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
import useReactRouter from 'use-react-router';
import validate from 'validate.js';
import { LoadingOverlay } from '../../../components/LoadingOverlay';
import updateForgottenPassword from './updateForgottenPassword.api';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        height: '100%',
    },
    grid: {
        height: '100%'
    },
    content: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },

    contentHeader: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: theme.spacing(5),
        paddingBototm: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    },
    contentBody: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center'
        }
    },
    form: {
        paddingLeft: 100,
        paddingRight: 100,
        paddingBottom: 125,
        [theme.breakpoints.up('md')]: {
          flexBasis: 700,
        },
        [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(2)
        }
    },
    title: {
        marginTop: theme.spacing(3)
    },
    textField: {
        marginTop: theme.spacing(2)
    },
    forgotButton: {
        margin: theme.spacing(2, 0)
    }
}));

export const ResetPassword = () => {

    const [sendUpdateForgottenPassword, updateForgottenPasswordData] = updateForgottenPassword();
    const classes = useStyles();
    const {history, match}: RouteComponentProps<{ token: string, email: string, expire: string }> = useReactRouter();
    const {token, email, expire} = match.params;
    const {enqueueSnackbar} = useSnackbar();
    const t = useT();

    const validationSchema = {
        password: {
            presence: {allowEmpty: false, message: t('is required')},
            length: {
                message: t('Your password must be at least 8 characters.'),
                minimum: 8,
                maximum: 128,
            }
        },
        password_confirmation: {
            presence: {allowEmpty: false, message: t('is required')},
            equality: {
                attribute: 'password',
                message: t('Must match your password'),
            }
        }
    };

    //validate email and token, redirect to login if no good.
    //http://localhost/password/reset
    if (!email || !token || !expire) {
        console.log('reset url not valid: ', match.params);
        history.push('/');
    }

    const expiryDate = moment(expire!, 'X');

    if (expiryDate.diff(moment(), 'minutes') <= 0) {
        //expired
        const expired = t('This password reset request has expired. Please try again.').toString();
        enqueueSnackbar(expired, {variant: 'error'});
        history.push(`/login?message=3`);
    }

    const [formState, setFormState] = useState({
        isValid: false,
        values: {
            password: '',
            password_confirmation: '',
        },
        touched: {
            password: false,
            password_confirmation: false,
        },
        errors: {
            password: null,
            password_confirmation: null,
        }
    });

    const handleChange = (event: any) => {
        event.persist();

        setFormState(state => ({
            ...state,
            values: {
                ...state.values,
                [event.target.name]: event.target.value
            },
            touched: {
                ...state.touched,
                [event.target.name]: true
            }
        }));
    };

    useEffect(() => {
        const errors = validate(formState.values, validationSchema);

        setFormState(state => ({
            ...state,
            isValid: !errors,
            errors: errors || {}
        }));
    }, [formState.values]);

    const hasError = (field: string) => {
        // @ts-ignore
        return !!(formState.touched[field] && formState.errors[field]);
    };

    const onFormSubmit = (event: any) => {
        event.preventDefault();

        const data = {
            email: decodeURIComponent(email!),
            token: token!,
            password: formState.values.password,
            password_confirmation: formState.values.password_confirmation,
        };

        sendUpdateForgottenPassword({variables: {data}})
            .then((response) => {
                const status = response.data!.updateForgottenPassword!.status;
                let messageId = '';

                switch (status) {
                    case 'PASSWORD_UPDATED':
                        messageId = '1';
                      enqueueSnackbar(t('Your password has been updated. Please login.').toString(), {variant: 'success'});
                        break;
                    case 'NOT_UPDATED':
                        messageId = '2';
                      enqueueSnackbar(t('There was an error updating your password. Please try again.').toString(), {variant: 'error'});
                        break;
                    default:
                        console.log(response);
                        break;
                }

                history.push(`/login?message=${messageId}`);
            })
          .catch(() => {
            enqueueSnackbar(t('There was an error communicating with the server.'), {variant: 'error'});
          });
    };

    return (
        <LoadingOverlay loading={updateForgottenPasswordData.loading}>
            <Grid item className={classes.content} lg={12} xs={12}>
                <div className={classes.content}>
                    <div className={classes.contentHeader}/>
                    <div className={classes.contentBody}>
                        <form className={classes.form} onSubmit={onFormSubmit}>
                            <Typography className={classes.title} variant="h2"><T phrase="Reset Password"/></Typography>

                            {updateForgottenPasswordData.error && (
                                <Typography color="error" className={classes.title} variant="h4">
                                    {updateForgottenPasswordData.error.message}
                                </Typography>
                            )}

                            <TextField
                                className={classes.textField}
                                error={hasError('password')}
                                fullWidth
                                helperText={
                                    hasError('password') ? formState.errors.password![0] : null
                                }
                                label={t('Password')}
                                name="password"
                                onChange={handleChange}
                                type="password"
                                value={formState.values.password || ''}
                                variant="outlined"
                            />

                            <TextField
                                className={classes.textField}
                                error={hasError('password_confirmation')}
                                fullWidth
                                helperText={
                                    hasError('password_confirmation') ? formState.errors.password_confirmation![0] : null
                                }
                                label={t('Confirm Password')}
                                name="password_confirmation"
                                onChange={handleChange}
                                type="password"
                                value={formState.values.password_confirmation || ''}
                                variant="outlined"
                            />

                            <Button
                                className={classes.forgotButton}
                                color="primary"
                                disabled={!formState.isValid}
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                            >
                                <T phrase="Submit"/>
                            </Button>
                            <Typography color="textSecondary" variant="body1">
                                <T phrase="Remembered your password?"/>&nbsp;
                                <Link color="textPrimary" variant="h6" component={RouterLink} to="/"><T phrase="Log In"/></Link>
                            </Typography>
                        </form>
                    </div>
                </div>
            </Grid>

        </LoadingOverlay>
    );
};

export default ResetPassword;
